import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_LANG,
  getBaseLang,
  getLocaleTag,
  isSupportedLang,
  LANG_STORAGE_KEY,
  resolveBrowserLang,
  SupportedLang,
} from './i18n.const';

/** Comment la langue demandée a été choisie. */
export interface LanguageChangeOptions {
  /**
   * Vrai si le visiteur a choisi cette langue explicitement.
   *
   * @remarks
   * Un choix explicite est mémorisé sur l'appareil et **ne peut plus être écrasé** par la langue
   * du compte à la connexion suivante — décision de Q. Perret : un geste délibéré prime sur une
   * préférence de fiche utilisateur.
   */
  manual?: boolean;
}

/**
 * Langue active de l'application : résolution, bascule à chaud et mémorisation.
 *
 * @remarks
 * Ordre de résolution au démarrage : choix manuel mémorisé, puis langue du compte connecté
 * (posée par `AuthService` après la lecture du profil), puis langue du navigateur, puis français.
 *
 * Cette application ne connaît pas les fichiers de traduction par client de Front
 * (`{lang}-{clientId}.json`) : une fiche publique n'a pas de vocabulaire propre à un client.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);

  private readonly _current = signal<SupportedLang>(DEFAULT_LANG);

  /** Langue active. */
  public readonly current = this._current.asReadonly();

  /** Étiquette de locale correspondante, pour le formatage des dates et des nombres. */
  public readonly locale = computed(() => getLocaleTag(this._current()));

  /**
   * Résout et applique la langue de départ.
   *
   * @returns La langue retenue.
   * @remarks
   * Appelée par l'initialiseur d'application : elle doit être **attendue** avant le premier rendu,
   * sinon l'écran s'affiche en français puis clignote vers la langue cible.
   */
  public async initialize(): Promise<SupportedLang> {
    const stored = this.readStoredLang();
    const lang = stored ?? resolveBrowserLang();

    await this.apply(lang);
    return lang;
  }

  /**
   * Applique une langue.
   *
   * @param code Code de langue, brut : `de`, `de-67` ou `null` sont acceptés.
   * @param options Voir {@link LanguageChangeOptions}.
   * @remarks
   * Sans effet si un choix manuel est déjà mémorisé et que l'appel ne vient pas du visiteur :
   * c'est ce qui permet à `AuthService` d'appeler cette méthode à chaque connexion sans écraser
   * le sélecteur de la barre haute.
   */
  public async use(code: string | null | undefined, options: LanguageChangeOptions = {}): Promise<void> {
    if (!options.manual && this.readStoredLang() !== null) return;

    const lang = getBaseLang(code);
    if (options.manual) this.storeLang(lang);
    if (lang === this._current()) return;

    await this.apply(lang);
  }

  /** Charge la langue, la rend active et l'annonce au document. */
  private async apply(lang: SupportedLang): Promise<void> {
    await firstValueFrom(this.translate.use(lang));

    this._current.set(lang);

    // Le `lang` de la page pilote la césure, la voix de synthèse et les correcteurs :
    // `index.html` le fige à `fr`, il faut donc le corriger à l'exécution
    document.documentElement.lang = lang;
  }

  /** Choix explicite mémorisé pour cet appareil, ou `null`. */
  private readStoredLang(): SupportedLang | null {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      return isSupportedLang(stored) ? stored : null;
    } catch {
      // Navigation privée, stockage refusé : on retombe simplement sur la résolution automatique
      return null;
    }
  }

  private storeLang(lang: SupportedLang): void {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // Le choix ne survivra pas au rechargement, mais la bascule fonctionne pour cette session
    }
  }
}
